﻿import type { Nullable } from '@src/shared/types/common';
import type { RouteSnapshot } from '@legacy/scripts/modules/yandex-route-cache';
import { YandexRouteCache } from '@legacy/scripts/modules/yandex-route-cache';
import type { IBackPlacesServiceInformation } from '@legacy/scripts/types/modules/back-places-service';
import type {
    ClustererGeoOption,
    IntermediatePoint,
    MultiRouteData,
    RouteInfoSource,
    YandexMapSettings
} from '@legacy/scripts/types/modules/yandex-map';

/**
 * Обёртка над картой Яндекса: создание карты, плейсмарки, GPS-трек, кластеры и
 * маршруты. Построение/кэширование/отрисовку маршрутов делегирует YandexRouteCache.
 */
export class YandexMap {
    private id!: string;
    private state!: ymaps.IMapState;
    private settings!: YandexMapSettings;
    public map!: ymaps.Map;
    private placemark: Nullable<ymaps.Placemark> = null;
    private readonly readyPromise: Promise<void>;
    private currentLocationPlacemark: Nullable<ymaps.Placemark> = null;

    // Сервис построения/кэша/отрисовки маршрутов.
    private readonly routeCache = new YandexRouteCache();

    // Снимок последнего построенного маршрута + ключ его координат (сохраняется на
    // бэкенд при сохранении попапа).
    private pendingRouteSnapshot: Nullable<RouteSnapshot> = null;
    private pendingRouteKey = '';

    // Текущая ломаная GPS-трека (для блока «Карта»).
    private trackPolyline: Nullable<ymaps.Polyline> = null;

    // Ссылка на маршрут для кнопки «Открыть в Яндекс.Картах»: контейнер карты → ссылка.
    private static readonly mapsButtonUrls = new WeakMap<Element, string>();

    constructor(id: string, state: ymaps.IMapState = {}, settings: YandexMapSettings = {}) {
        this.readyPromise = new Promise<void>((resolve) => {
            window.ymaps.ready(() => {
                this.id = id;
                this.state = {
                    center: [56.75584199999999, 60.61596],
                    zoom: 8,
                    controls: [],

                    ...state
                };
                this.settings = settings;

                window[`${id}Map`]?.destroy();

                this.map = window[`${id}Map`] = new window.ymaps.Map(this.id, this.state, {
                    yandexMapDisablePoiInteractivity: true
                });

                if (settings.type === 'one-placemark') {
                    if (state.center) {
                        this.updatePlacemark(state.center, settings.pointAddress);
                    }

                    if (settings.mapClickable) {
                        this.map.events.add('click', (e: ymaps.MapEvent) => {
                            this.updatePlacemark(e.get('coords'));
                        });
                    }
                }

                resolve();
            });
        });
    }

    private getNewPlacemark(coords: number[]): ymaps.Placemark {
        const properties = {
            iconCaption: window.getLocaleDict().yandexMap.searchPlaceholder
        };

        const options = {
            preset: 'islands#redDotIcon',
            draggable: true
        };

        return new window.ymaps.Placemark(coords, properties, options);
    }

    updatePlacemark(coords: number[], text?: string): void {
        const updateInfoPlacemark = (placemarkCoords: number[]) => {
            const properties = this.placemark?.properties;

            if (text) {
                properties?.set({ iconCaption: text });
            }
            else {
                properties?.set('iconCaption', window.getLocaleDict().yandexMap.searchPlaceholder);
                this.settings.onUpdatePlacemarkAfter?.();

                const service = window.isRussianCity
                    ? window.BackDadataService
                    : window.BackPlacesService;

                service.getObjectByCoordinates(placemarkCoords, (result) => {
                    if (result) {
                        properties?.set({ iconCaption: result.fullAddress });
                        this.settings.onUpdatePlacemarkBefore?.();
                    }
                });
            }
        };

        if (this.placemark) {
            this.placemark.geometry?.setCoordinates(coords);
        }
        else {
            this.placemark = this.getNewPlacemark(coords);
            this.map.geoObjects.add(this.placemark);
            this.placemark.events.add('dragend', () => {
                updateInfoPlacemark(this.placemark?.geometry?.getCoordinates() as number[]);
            });
        }

        updateInfoPlacemark(coords);
    }

    setZoom(newZoom: number): void {
        void this.readyPromise.then(() => this.map.setZoom(newZoom));
    }

    setCenter(coords: number[]): void {
        void this.readyPromise.then(() => this.map.setCenter(coords));
    }

    fitToViewport(): void {
        void this.readyPromise.then(() => this.map.container.fitToViewport());
    }

    /**
     * Рисует одиночный маршрут по координатам. Если маршрут уже есть в кэше
     * (бэкенд или локальный) — рисует из снимка и передаёт в callback «заглушку»;
     * иначе строит через Яндекс и передаёт «живой» объект маршрута.
     *
     * @param coords координаты точек маршрута
     * @param callback вызывается с метриками маршрута (длина/время по путям и в целом);
     * объект может быть заглушкой, поэтому остальной API ymaps.router.Route недоступен
     * @param onError необязательный обработчик ошибки
     */
    setRoute(
        coords: number[][],
        callback?: (routes: RouteInfoSource) => void,
        onError?: (err: unknown) => void
    ): void {
        void this.readyPromise.then(() => {
            this.removeGeoObjects();
            this.setMapsButtonLink(coords);

            void this.routeCache.obtainSnapshot(coords).then(
                ({ snapshot, routes }) => {
                    // Настоящий объект маршрута — только у первого построителя.
                    if (routes) {
                        this.map.geoObjects.add(routes);
                        // Типы @types/yandex-maps отдают пути как IGeoObject[], хотя
                        // getLength/getTime у них есть — сужаем до RouteInfoSource.
                        callback?.(routes as unknown as RouteInfoSource);
                        return;
                    }

                    // Кэш: рисуем сохранённую линию и отдаём в callback «заглушку».
                    this.routeCache.renderSnapshot(this.map, snapshot);
                    callback?.(this.routeCache.createRouteShim(snapshot));
                },
                onError
            );
        });
    }

    // Рисует (заменяет) синюю ломаную GPS-трека — например, путь ТС по ГЛОНАСС.
    updateTrack(coords: number[][], color: string): void {
        void this.readyPromise.then(() => {
            this.removeTrack();

            const polyline = new window.ymaps.Polyline(coords, {}, {
                strokeColor: color,
                strokeWidth: 4,
                zIndex: 2000
            });

            this.trackPolyline = polyline;
            this.map.geoObjects.add(polyline);
        });
    }

    // Убирает ломаную GPS-трека с карты (например, когда обновление не вернуло данных).
    removeTrack(): void {
        if (this.trackPolyline) {
            this.map.geoObjects.remove(this.trackPolyline);
            this.trackPolyline = null;
        }
    }

    /**
     * Рисует несколько маршрутов на одной карте (блок «Карта» с ГЛОНАСС и приложением).
     * Для каждого маршрута из routesArray получает снимок (см. obtainSnapshot) и рисует
     * линию своим цветом + (при showPoints) пронумерованные метки с адресами. Границы
     * карты подгоняются один раз после отрисовки всех маршрутов, если хоть один пришёл
     * из кэша.
     */
    setMultiRoute(routesArray: MultiRouteData[], callback?: (routes: ymaps.router.Route) => void): void {
        void this.readyPromise.then(() => {
            this.removeGeoObjects();

            // Кнопка одна, поэтому при нескольких маршрутах ведём на первый;
            this.setMapsButtonLink(
                routesArray.length ? routesArray[0].points.map((point) => point.coords) : []
            );

            const allCoords: number[][] = [];
            let pendingCount = routesArray.length;
            let needManualBounds = false;

            const settle = () => {
                if (--pendingCount === 0 && needManualBounds) {
                    this.routeCache.setBoundsForCoords(this.map, allCoords);
                }
            };

            routesArray.forEach((routeData) => {
                const coords = routeData.points.map((p) => p.coords);

                void this.routeCache.obtainSnapshot(coords).then(
                    ({ snapshot, routes }) => {
                        if (routes) {
                            routes.getPaths().toArray().forEach((path: ymaps.IGeoObject) => {
                                path.options.set({ strokeColor: routeData.colorForRoute });
                            });

                            this.map.geoObjects.add(routes);

                            // Скрываем собственные точки маршрута Яндекса: номера рисуем
                            // сами (с адресом в балуне), чтобы не было двух наборов точек.
                            routes.options.set({ wayPointVisible: false });

                            if (routeData.showPoints) {
                                routeData.points.forEach((point, i) => {
                                    if (point.address) {
                                        const placemark = new window.ymaps.Placemark(point.coords, {
                                            balloonContent: point.address,
                                            hintContent: point.address,
                                            iconContent: (i + 1).toString()
                                        });

                                        this.map.geoObjects.add(placemark);
                                    }
                                });
                            }

                            callback?.(routes);
                        }
                        else {
                            needManualBounds = true;
                            this.routeCache.renderMultiRouteItem(this.map, snapshot, routeData, allCoords);
                        }

                        settle();
                    },
                    () => {
                        settle();
                    }
                );
            });
        });
    }

    /**
     * Строит маршрут заново (минуя кэш) и запоминает его снимок. Ничего не сохраняет
     * на бэкенд — снимок сохраняется отдельно через savePendingRoute при сохранении
     * попапа (после прохождения всех проверок).
     *
     * @param coords координаты точек маршрута
     * @param callback вызывается с готовым маршрутом при успехе
     * @param onError вызывается при ошибке построения (иначе пишем в console.error).
     * Вьюшка обязана обработать ошибку, иначе ожидающий её промис не разрешится.
     */
    public buildRoute(
        coords: number[][],
        callback?: (routes: ymaps.router.Route) => void,
        onError?: (err: unknown) => void
    ): void {
        void this.readyPromise.then(() => {
            this.removeGeoObjects();
            this.setMapsButtonLink(coords);

            this.routeCache.buildSnapshot(coords).then(
                ({ snapshot, routes }) => {
                    this.map.geoObjects.add(routes);
                    this.pendingRouteSnapshot = snapshot;
                    this.pendingRouteKey = YandexRouteCache.getRouteKey(coords);
                    callback?.(routes);
                },
                (err: unknown) => {
                    console.error('YandexMap route build error:', err);
                    onError?.(err);
                }
            );
        });
    }

    /**
     * Сохраняет маршрут на бэкенд при сохранении попапа (вместе с UpdateRouteUnit).
     * Ключ считается из точек. Если маршрут уже построен в этой сессии (кнопкой
     * «Построить маршрут») — сохраняем его снимок. Иначе идём через obtainSnapshot:
     * он проверит локальный кэш и бэкенд и только при промахе построит маршрут
     * (и сам его сохранит) — так сохранение без изменения точек не тратит запрос в Яндекс.
     */
    public savePendingRoute(coords: number[][]): void {
        if (this.pendingRouteSnapshot && this.pendingRouteKey === YandexRouteCache.getRouteKey(coords)) {
            this.routeCache.saveToBackend(coords, this.pendingRouteSnapshot);
            return;
        }

        void this.routeCache.obtainSnapshot(coords).catch(() => {
            console.warn('YandexMap: не удалось построить маршрут для сохранения');
        });
    }

    /**
     * Ставит ссылку на маршрут в кнопку «Открыть в Яндекс.Картах».
     *
     * @param coords координаты точек маршрута в порядке следования; пустой массив —
     * на карте маршрутов нет, ссылку сбрасываем
     */
    private setMapsButtonLink(coords: number[][]): void {
        const container = this.map.container.getElement();

        if (!YandexMap.mapsButtonUrls.has(container)) {
            // Клик по кнопке «Открыть в Яндекс.Картах»: гасим обработчик API и открываем свой маршрут.
            container.addEventListener(
                'click',
                (event: MouseEvent) => {
                    const target = event.target;
                    const container = event.currentTarget;
                    const yandexMapsUrl = container instanceof Element ? YandexMap.mapsButtonUrls.get(container) : undefined;

                    // Кнопка «Открыть в Яндекс.Картах» в левом нижнем углу карты — gotoymaps
                    if (!yandexMapsUrl || !(target instanceof Element) || !target.closest('[class*="gotoymaps"]')) {
                        return;
                    }

                    event.stopPropagation();
                    window.open(yandexMapsUrl, '_blank', 'noopener');
                },
                true
            );
        }

        YandexMap.mapsButtonUrls.set(container, YandexMap.getRouteMapsUrl(coords));
    }

    // Ссылка на маршрут в Яндекс.Картах: rtext — точки в формате «широта,долгота» через «~»,
    // rtt — тип маршрута (auto). Без rtext Яндекс открывает только метки.
    private static getRouteMapsUrl(coords: number[][]): string {
        if (coords.length < 2) {
            return '';
        }

        const routePoints = coords.map(([lat, lng]) => `${lat},${lng}`).join('~');

        return `https://yandex.ru/maps/?rtext=${routePoints}&rtt=auto`;
    }

    /**
     * Очищает карту от всех объектов, кроме метки текущего местоположения.
     * Трек тоже считается «лишним» при полной очистке (сбрасывает trackPolyline).
     */
    removeGeoObjects(): void {
        // Трек тоже считается «лишним» объектом при полной очистке карты.
        this.trackPolyline = null;

        if (this.currentLocationPlacemark) {
            const toRemove: ymaps.IGeoObject[] = [];
            this.map.geoObjects.each((obj) => {
                if (obj !== this.currentLocationPlacemark) {
                    toRemove.push(obj);
                }
            });
            toRemove.forEach((obj) => this.map.geoObjects.remove(obj));
        }
        else {
            this.map.geoObjects.removeAll();
        }
    }

    updateCurrentLocationPlacemark(coords: number[]): void {
        if (this.currentLocationPlacemark) {
            this.currentLocationPlacemark.geometry?.setCoordinates(coords);
            return;
        }

        this.currentLocationPlacemark = new window.ymaps.Placemark(
            coords,
            {
                iconContent: '🚗'
            },
            {
                preset: 'islands#redStretchyIcon',
                zIndex: 1000
            }
        );
        this.map.geoObjects.add(this.currentLocationPlacemark);
    }

    removeCurrentLocationPlacemark(): void {
        if (this.currentLocationPlacemark) {
            this.map.geoObjects.remove(this.currentLocationPlacemark);
            this.currentLocationPlacemark = null;
        }
    }

    addComboboxSelectHandler(combobox: kendo.ui.ComboBox | kendo.ui.AutoComplete): void {
        combobox.unbind('select');

        combobox.bind('select', (autocomplete: kendo.ui.AutoCompleteSelectEvent) => {
            const { value, text } = autocomplete.dataItem;

            const resolveCoords = (
                rawValue: string,
                callback: (info: Nullable<IBackPlacesServiceInformation>) => void
            ) => {
                window.isRussianCity
                    ? window.BackDadataService.getObjectByAddress(rawValue, callback)
                    : window.BackPlacesService.getObjectByPlaceId(rawValue, callback);
            };

            resolveCoords(value, (result: Nullable<IBackPlacesServiceInformation>) => {
                if (!result) {
                    return;
                }

                this.setZoom(16);
                this.setCenter(result.coords);
                this.updatePlacemark(result.coords, text);
            });
        });
    }

    setClusterer(geoOptionsArray: ClustererGeoOption[]): void {
        void this.readyPromise.then(() => {
            this.removeGeoObjects();

            const geoObjects: ymaps.GeoObject[] = [];

            geoOptionsArray.forEach(({ coords, title, description }) => {
                geoObjects.push(new window.ymaps.GeoObject({
                    geometry: {
                        type: 'Point',
                        coordinates: coords
                    },
                    properties: {
                        clusterCaption: title,
                        balloonContentBody: description
                    }
                }));
            });

            const clusterer = new window.ymaps.Clusterer({ clusterDisableClickZoom: true });
            clusterer.add(geoObjects);

            this.map.geoObjects.add(clusterer);
        });
    }

    // Убирает неразрывные пробелы из человекочитаемых строк Яндекса («1&#160;234 км»).
    static removeNonBreakingSpaces(text: string): string {
        return text.replaceAll('&#160;', '');
    }

    /**
     * Единственный форматтер длины маршрута: «750 м», «12,3 км».
     */
    static formatHumanLength(meters: number): string {
        if (meters < 1000) {
            return `${Math.round(meters)} м`;
        }

        return `${(meters / 1000).toFixed(1).replace('.', ',')} км`;
    }

    /**
     * Единственный форматтер времени маршрута с днями: «18 мин», «1 ч 20 мин», «5 дн 18 ч».
     */
    static formatHumanTime(seconds: number): string {
        const totalMinutes = Math.floor(seconds / 60);
        const days = Math.floor(totalMinutes / 1440);
        const hours = Math.floor((totalMinutes % 1440) / 60);
        const minutes = totalMinutes % 60;

        const parts: string[] = [];

        if (days > 0) {
            parts.push(`${days} дн`);
        }

        if (hours > 0) {
            parts.push(`${hours} ч`);
        }

        if (minutes > 0 || parts.length === 0) {
            parts.push(`${minutes} мин`);
        }

        return parts.join(' ');
    }

    /**
     * Формирует текст для textarea routeInfo: расстояния между точками, общее
     * расстояние и время. Используется и в попапе, и в гридах показа маршрута.
     *
     * Длина/время считаются из числовых значений едиными форматтерами
     * (formatHumanLength/formatHumanTime), поэтому текст одинаков и для живого
     * маршрута, и для маршрута из кэша. Подписи берутся из JS-справочника локали.
     */
    static formatRouteInfo(routes: RouteInfoSource, vehicleUsageMinutes: number): string {
        const labels = window.getLocaleDict().routeInfo;

        const pathDescriptions = routes.getPaths().toArray().map((route, idx) => {
            const distance = YandexMap.formatHumanLength(route.getLength());
            const time = YandexMap.formatHumanTime(route.getTime());

            return `${labels.fromPoint} ${idx + 1} ${labels.toPoint} ${idx + 2}: ${labels.distance} - ${distance}, ${labels.time} - ${time}`;
        });

        const totalDistance = YandexMap.formatHumanLength(routes.getLength());
        const totalTime = YandexMap.formatHumanTime(routes.getTime());

        return [
            ...pathDescriptions,
            `${labels.totalDistance}: ${totalDistance}`,
            `${labels.totalTime}: ${totalTime}`,
            `${labels.vehicleUsage}: ${vehicleUsageMinutes}`
        ].join('\n');
    }

    // Обёртка над ymaps.route. Делегирует в YandexRouteCache, чтобы реализация
    // платного routing-запроса была одна (там она нужна для buildSnapshot).
    static route(coords: number[][], callback: (routes: ymaps.router.Route) => void, onError?: (err: unknown) => void): void {
        YandexRouteCache.route(coords, callback, onError);
    }

    /**
     * Возвращает промежуточные точки вдоль маршрута с заданным шагом (в метрах),
     * интерполируя координаты по сегментам путей.
     */
    static getIntermediatePoints(route: ymaps.router.Route, step = 700): IntermediatePoint[] {
        const routeLength = route.getLength();
        const segments: ymaps.RouteSegment[] = [];
        route.getPaths().toArray().forEach((path: ymaps.RoutePath) => segments.push(...path.getSegments()));

        function getIntermediatePoint(distance: number): IntermediatePoint {
            let segmentIndex = 0;
            let segmentLength = segments[segmentIndex].getLength();

            while (distance > segmentLength) {
                distance -= segmentLength;
                segmentIndex++;
                segmentLength = segments[segmentIndex].getLength();
            }

            const coordinates: number[][] = segments[segmentIndex].getCoordinates();
            const segmentRatio = distance / segmentLength;
            const indexOnSegment = Math.floor(segmentRatio * (coordinates.length - 1));

            const [latitude, longitude] = coordinates[indexOnSegment];
            return { latitude, longitude };
        }

        const intermediatePoints: IntermediatePoint[] = [];

        for (let distance = step; distance < routeLength; distance += step) {
            intermediatePoints.push(getIntermediatePoint(distance));
        }

        return intermediatePoints;
    }
}

window.YandexMap = YandexMap;

